import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminPages.css';

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', image: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/api/admin/categories');
      setCategories(res.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const openAddForm = () => {
    setForm({ name: '', description: '', image: '' });
    setEditingId(null);
    setError('');
    setShowForm(true);
  };

  const openEditForm = (cat) => {
    setForm({ name: cat.name, description: cat.description || '', image: cat.image || '' });
    setEditingId(cat._id);
    setError('');
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setError(''); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name) { setError('Category name is required'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`/api/admin/categories/${editingId}`, form);
      } else {
        await axios.post('/api/admin/categories', form);
      }
      closeForm();
      fetchCategories();
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving category');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete category "${name}"?`)) return;
    try {
      await axios.delete(`/api/admin/categories/${id}`);
      fetchCategories();
    } catch (err) {
      alert('Error deleting category');
    }
  };

  if (loading) return <div className="admin-loading">Loading...</div>;

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">Categories</h2>
          <p className="admin-page-desc">Manage material categories. ({categories.length} total)</p>
        </div>
        <button className="admin-btn admin-btn-primary" onClick={openAddForm}>+ Add Category</button>
      </div>

      {showForm && (
        <div className="admin-modal-overlay" onClick={closeForm}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{editingId ? 'Edit Category' : 'Add New Category'}</h3>
              <button className="admin-modal-close" onClick={closeForm}>&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="admin-material-form">
              {error && <div className="admin-form-error">{error}</div>}
              <div className="admin-form-group">
                <label>Name *</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>
              <div className="admin-form-group">
                <label>Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows="3" />
              </div>
              <div className="admin-form-group">
                <label>Image URL</label>
                <input type="text" value={form.image} onChange={e => setForm({ ...form, image: e.target.value })} />
              </div>
              <div className="admin-form-actions">
                <button type="button" className="admin-btn admin-btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="admin-btn admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Update' : 'Add Category'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="admin-page-card admin-table-card">
        <div className="admin-notifications-table">
          <table>
            <thead>
              <tr><th>Image</th><th>Name</th><th>Description</th><th>Materials</th><th>Created</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {categories.length === 0 ? (
                <tr><td colSpan="6" className="admin-no-data">No categories yet.</td></tr>
              ) : categories.map(cat => (
                <tr key={cat._id}>
                  <td>{cat.image ? <img src={cat.image} alt={cat.name} className="admin-material-thumb" /> : '--'}</td>
                  <td><strong>{cat.name}</strong></td>
                  <td>{cat.description || '--'}</td>
                  <td>{cat.materialCount ?? 0}</td>
                  <td>{new Date(cat.createdAt).toLocaleDateString()}</td>
                  <td>
                    <div className="admin-action-buttons">
                      <button className="admin-btn-icon admin-btn-edit" onClick={() => openEditForm(cat)}>Edit</button>
                      <button className="admin-btn-icon admin-btn-delete" onClick={() => handleDelete(cat._id, cat.name)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Categories;
